import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import type { DateValueType } from "react-tailwindcss-datepicker";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog, { type Ref } from "@/components/Dialog";
import ListItem from "@/components/ListItem";
import Select from "@/components/Select";
import { COMMITTEE_ASSIGNMENT_TITLES_QUERY } from "../queries";

export type EditCommitteeMemberValue = {
  id: string;
  startDate: string | null;
  endDate: string | null;
  title: {
    id?: string;
    title: string;
  };
  person: {
    pgaId?: string | null;
    displayName?: string | null;
    profilePhoto?: string | null;
  } | null;
  supportingText?: string;
};

export type EditCommitteeAssignmentSaveInput = {
  id: string;
  titleId: string;
  title: string;
  startDate: string;
  endDate: string | null;
};

export type EditCommitteeAssignmentDialogProps = {
  open: boolean;
  member: EditCommitteeMemberValue | null;
  saving?: boolean;
  saveLabel?: string;
  onClose(): void;
  onSave(input: EditCommitteeAssignmentSaveInput): void;
};

type FormState = {
  startDate: string;
  endDate: string;
  titleId: string;
};

const EMPTY_FORM: FormState = {
  startDate: "",
  endDate: "",
  titleId: ""
};

function toIsoDate(value: Date | string | null | undefined): string {
  if (!value) return "";
  if (value instanceof Date) return DateTime.fromJSDate(value).toISODate() ?? "";
  if (typeof value === "string") {
    const dt = DateTime.fromISO(value, { zone: "utc" });
    return dt.isValid ? (dt.toISODate() ?? "") : "";
  }
  return "";
}

function dateSelectStartToIso(value: DateValueType | string | null): string {
  if (value == null || typeof value === "string") return "";
  return toIsoDate(value.startDate);
}

export default function EditCommitteeAssignmentDialog({
  open,
  member,
  saving = false,
  saveLabel = "Save",
  onClose,
  onSave
}: EditCommitteeAssignmentDialogProps) {
  const dialogRef = useRef<Ref>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const {
    data: titlesData,
    loading: titlesLoading,
    error: titlesError
  } = useQuery(COMMITTEE_ASSIGNMENT_TITLES_QUERY, {
    skip: !open
  });

  const titles = titlesData?.committeeAssignmentTitles;
  const titleOptions = (titles ?? []).map((title) => ({
    value: title.id,
    label: title.title
  }));

  const memberId = member?.id;
  const memberStartDate = member?.startDate ?? null;
  const memberEndDate = member?.endDate ?? null;
  const memberTitleId = member?.title.id;
  const memberTitleName = member?.title.title;

  useEffect(() => {
    if (!open || memberId == null) return;

    setForm({
      startDate: memberStartDate ?? "",
      endDate: memberEndDate ?? "",
      titleId: memberTitleId ?? ""
    });
  }, [open, memberId, memberStartDate, memberEndDate, memberTitleId, memberTitleName]);

  useEffect(() => {
    if (!open || memberId == null || !titles?.length) return;

    setForm((current) => {
      if (current.titleId) return current;

      const matchedTitle =
        (memberTitleId ? titles.find((title) => title.id === memberTitleId) : undefined) ??
        (memberTitleName ? titles.find((title) => title.title === memberTitleName) : undefined);

      return matchedTitle ? { ...current, titleId: matchedTitle.id } : current;
    });
  }, [open, memberId, memberTitleId, memberTitleName, titles]);

  const endBeforeStart = !!form.startDate && !!form.endDate && form.endDate < form.startDate;
  const selectedTitle = titles?.find((title) => title.id === form.titleId);
  const isValid = !!member && form.startDate.length > 0 && !!selectedTitle && !endBeforeStart;
  const startDateValue = useMemo(() => ({ startDate: form.startDate, endDate: form.startDate }), [form.startDate]);
  const endDateValue = useMemo(() => ({ startDate: form.endDate, endDate: form.endDate }), [form.endDate]);

  const handleRequestClose = () => {
    if (saving) return;
    dialogRef.current?.close();
  };

  const handleSave = () => {
    if (!member || !isValid || saving || !selectedTitle) return;

    onSave({
      id: member.id,
      titleId: selectedTitle.id,
      title: selectedTitle.title,
      startDate: form.startDate,
      endDate: form.endDate || null
    });
  };

  return (
    <Dialog
      ref={dialogRef}
      open={open}
      headline="Edit Committee Member"
      onClosed={onClose}
      hasFormElements
      displayOverflow
      className="md:w-[560px]"
      actions={
        <>
          <Button
            variant="text"
            onClick={handleRequestClose}
            disabled={saving}
            testId="edit-committee-assignment-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={!isValid || saving || titlesLoading || !!titlesError}
            testId="edit-committee-assignment-save"
          >
            {saveLabel}
          </Button>
        </>
      }
    >
      {member && (
        <div className="min-w-[320px] space-y-6">
          <ListItem
            useAvatar
            compact
            picture={member.person?.profilePhoto ?? undefined}
            iconName="person"
            overline={member.person?.pgaId || undefined}
            headline={member.person?.displayName ?? "Unknown Participant"}
            supportingText={member.supportingText}
          />

          <Select
            label="Position"
            value={form.titleId}
            options={titleOptions}
            disabled={!!titlesError || (!titlesLoading && titleOptions.length === 0)}
            onSelect={(value) => setForm((current) => ({ ...current, titleId: value }))}
          />

          {titlesError && (
            <p className="text-body-small text-sys-brand-error" role="alert">
              Unable to load positions. {titlesError.message}
            </p>
          )}

          <div className="grid w-full min-w-0 grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4">
            <DateSelect
              label="Start Date"
              value={startDateValue}
              onChange={(value) => {
                const startDate = dateSelectStartToIso(value);
                if (!startDate) return;
                setForm((current) => ({ ...current, startDate }));
              }}
              useSingleDate
              useMultiCalendar={false}
              useModal
              showFooter={false}
            />

            <DateSelect
              label="End Date"
              value={endDateValue}
              onChange={(value) => {
                setForm((current) => ({ ...current, endDate: dateSelectStartToIso(value) }));
              }}
              useSingleDate
              useMultiCalendar={false}
              useModal
              showFooter={false}
              error={endBeforeStart}
              errorMessage={endBeforeStart ? "End date cannot be earlier than start date" : undefined}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
}
